import { useEffect, useState } from 'react';
import type { DocumentSummary } from '../api/types';
import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { ErrorBanner } from './ui/ErrorBanner';

/** Remove or legal hold/release: both need a written reason that goes to the audit trail. */
export function ReasonDialog({
  doc,
  kind,
  open,
  onClose,
  onSubmit,
}: {
  doc: DocumentSummary;
  kind: 'remove' | 'hold';
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<unknown>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const remove = kind === 'remove';
  return (
    <Dialog
      open={open}
      title={remove ? 'Remove document' : doc.legalHold ? 'Release legal hold' : 'Apply legal hold'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={remove ? 'danger' : 'primary'} disabled={reason.trim().length < 5} busy={busy} onClick={() => void submit()}>
            {remove ? 'Remove' : doc.legalHold ? 'Release hold' : 'Apply hold'}
          </Button>
        </>
      }
    >
      <p>
        {remove ? (
          <>
            This moves <strong>{doc.title}</strong> out of the folder and into quarantine. It is only possible inside the 7-day grace window and
            is written to the audit trail with your reason. Nothing is deleted.
          </>
        ) : doc.legalHold ? (
          'Releasing the hold lets retention and hide/remove apply again.'
        ) : (
          'A legal hold blocks every removal path, including retention purge, until it is released.'
        )}
      </p>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-muted-foreground">Reason (required)</span>
        <textarea
          className="gsl-input mt-1"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={remove ? 'e.g. uploaded to the wrong customer' : undefined}
        />
      </label>
      {error ? (
        <div className="mt-2">
          <ErrorBanner error={error} />
        </div>
      ) : null}
    </Dialog>
  );
}
